import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { Notification, NotificationFormData } from '../../../types/cms.types';
import { NotificationService } from '../../../service/supabase/NotificationService';

interface CpnCreateEditNotificationProps {
    visible: boolean;
    notification?: Notification | null;
    onHide: () => void;
    onSave: () => void;
}

export const CpnCreateEditNotification: React.FC<CpnCreateEditNotificationProps> = ({
    visible,
    notification,
    onHide,
    onSave
}) => {
    const [formData, setFormData] = useState<NotificationFormData>({
        title: '',
        body: '',
        topic: 'app_user',
        send_type: 'immediate',
        image_url: '',
        data: {}
    });
    const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [sendImmediately, setSendImmediately] = useState(false);

    useEffect(() => {
        if (notification) {
            setFormData({
                title: notification.title,
                body: notification.body,
                topic: notification.topic,
                send_type: notification.send_type,
                image_url: notification.image_url || '',
                data: notification.data || {}
            });
            if (notification.scheduled_at) {
                setScheduledDate(new Date(notification.scheduled_at));
            }
        } else {
            resetForm();
        }
    }, [notification, visible]);

    const resetForm = () => {
        setFormData({
            title: '',
            body: '',
            topic: 'app_user',
            send_type: 'immediate',
            image_url: '',
            data: {}
        });
        setScheduledDate(null);
        setErrors({});
        setSendImmediately(false);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Vui lòng nhập tiêu đề';
        }

        if (!formData.body.trim()) {
            newErrors.body = 'Vui lòng nhập nội dung';
        }

        if (formData.send_type === 'scheduled' && !scheduledDate) {
            newErrors.scheduled_at = 'Vui lòng chọn thời gian gửi';
        }

        if (formData.send_type === 'scheduled' && scheduledDate) {
            const now = new Date();
            if (scheduledDate <= now) {
                newErrors.scheduled_at = 'Thời gian gửi phải sau thời điểm hiện tại';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);

        try {
            const dataToSave: NotificationFormData = {
                ...formData,
                ...(formData.send_type === 'scheduled' && scheduledDate && {
                    scheduled_at: scheduledDate.toISOString()
                })
            };

            if (notification) {
                // Update existing notification
                await NotificationService.updateNotification(notification.id, dataToSave);
            } else {
                // Create new notification
                const newNotification = await NotificationService.createNotification(
                    dataToSave,
                    // Get current admin ID from context/localStorage
                    localStorage.getItem('adminId') || undefined
                );

                // Send immediately if checked
                if (sendImmediately && formData.send_type === 'immediate') {
                    await NotificationService.sendNotificationNow(newNotification.id);
                }
            }

            onSave();
            resetForm();
        } catch (error: any) {
            setErrors({ submit: error.message });
        } finally {
            setLoading(false);
        }
    };

    const topicOptions = [
        { label: '👤 Khách hàng (app_user)', value: 'app_user' },
        { label: '🚗 Tài xế (app_driver)', value: 'app_driver' }
    ];

    const footer = (
        <div>
            <Button
                label="Hủy"
                icon="pi pi-times"
                onClick={() => {
                    onHide();
                    resetForm();
                }}
                className="p-button-text"
                disabled={loading}
            />
            <Button
                label={notification ? 'Cập nhật' : 'Tạo'}
                icon="pi pi-check"
                onClick={handleSubmit}
                loading={loading}
                autoFocus
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '600px' }}
            header={
                <div className="flex items-center gap-2">
                    <i className="pi pi-bell text-primary"></i>
                    <span>{notification ? 'Chỉnh sửa thông báo' : 'Tạo thông báo mới'}</span>
                </div>
            }
            modal
            footer={footer}
            onHide={() => {
                onHide();
                resetForm();
            }}
        >
            <div className="space-y-4">
                {errors.submit && (
                    <Message severity="error" text={errors.submit} className="w-full" />
                )}

                {/* Title */}
                <div className="field">
                    <label htmlFor="title" className="block font-semibold mb-2">
                        Tiêu đề <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Nhập tiêu đề thông báo"
                        className={`w-full ${errors.title ? 'p-invalid' : ''}`}
                        maxLength={100}
                    />
                    {errors.title && <small className="text-red-500">{errors.title}</small>}
                    <small className="text-gray-500">
                        {formData.title.length}/100 ký tự
                    </small>
                </div>

                {/* Body */}
                <div className="field">
                    <label htmlFor="body" className="block font-semibold mb-2">
                        Nội dung <span className="text-red-500">*</span>
                    </label>
                    <InputTextarea
                        id="body"
                        value={formData.body}
                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                        placeholder="Nhập nội dung thông báo"
                        rows={4}
                        className={`w-full ${errors.body ? 'p-invalid' : ''}`}
                        maxLength={500}
                    />
                    {errors.body && <small className="text-red-500">{errors.body}</small>}
                    <small className="text-gray-500">
                        {formData.body.length}/500 ký tự
                    </small>
                </div>

                {/* Topic */}
                <div className="field">
                    <label htmlFor="topic" className="block font-semibold mb-2">
                        Gửi tới <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                        id="topic"
                        value={formData.topic}
                        options={topicOptions}
                        onChange={(e) => setFormData({ ...formData, topic: e.value })}
                        placeholder="Chọn đối tượng nhận"
                        className="w-full"
                    />
                </div>

                {/* Image URL */}
                <div className="field">
                    <label htmlFor="image_url" className="block font-semibold mb-2">
                        URL hình ảnh (tùy chọn)
                    </label>
                    <InputText
                        id="image_url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://example.com/image.png"
                        className="w-full"
                    />
                    <small className="text-gray-500">
                        URL hình ảnh sẽ hiển thị trong thông báo
                    </small>
                </div>

                {/* Send Type */}
                <div className="field">
                    <label className="block font-semibold mb-2">
                        Loại gửi <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center">
                            <RadioButton
                                inputId="immediate"
                                name="send_type"
                                value="immediate"
                                onChange={(e) => setFormData({ ...formData, send_type: e.value })}
                                checked={formData.send_type === 'immediate'}
                            />
                            <label htmlFor="immediate" className="ml-2">
                                <i className="pi pi-send text-green-500 mr-2"></i>
                                Gửi ngay lập tức
                            </label>
                        </div>
                        <div className="flex items-center">
                            <RadioButton
                                inputId="scheduled"
                                name="send_type"
                                value="scheduled"
                                onChange={(e) => setFormData({ ...formData, send_type: e.value })}
                                checked={formData.send_type === 'scheduled'}
                            />
                            <label htmlFor="scheduled" className="ml-2">
                                <i className="pi pi-clock text-blue-500 mr-2"></i>
                                Đặt lịch gửi
                            </label>
                        </div>
                    </div>
                </div>

                {/* Scheduled Date - only show if send_type is scheduled */}
                {formData.send_type === 'scheduled' && (
                    <div className="field">
                        <label htmlFor="scheduled_at" className="block font-semibold mb-2">
                            Thời gian gửi <span className="text-red-500">*</span>
                        </label>
                        <Calendar
                            id="scheduled_at"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.value as Date)}
                            showTime
                            hourFormat="24"
                            dateFormat="dd/mm/yy"
                            placeholder="Chọn ngày và giờ"
                            className={`w-full ${errors.scheduled_at ? 'p-invalid' : ''}`}
                            minDate={new Date()}
                        />
                        {errors.scheduled_at && (
                            <small className="text-red-500">{errors.scheduled_at}</small>
                        )}
                        <small className="text-gray-500">
                            Thông báo sẽ được gửi tự động vào thời gian đã chọn
                        </small>
                    </div>
                )}

                {/* Send Immediately checkbox - only show for new immediate notifications */}
                {!notification && formData.send_type === 'immediate' && (
                    <div className="field">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="send_immediately"
                                checked={sendImmediately}
                                onChange={(e) => setSendImmediately(e.target.checked)}
                                className="mr-2"
                            />
                            <label htmlFor="send_immediately">
                                <i className="pi pi-send text-green-500 mr-2"></i>
                                Gửi ngay sau khi tạo
                            </label>
                        </div>
                        <small className="text-gray-500 ml-6">
                            Nếu không chọn, thông báo sẽ ở trạng thái chờ và bạn có thể gửi sau
                        </small>
                    </div>
                )}

                {/* Info Message */}
                <Message
                    severity="info"
                    text={
                        formData.send_type === 'immediate'
                            ? '💡 Thông báo ngay lập tức sẽ được gửi ngay khi bạn nhấn nút "Gửi ngay" hoặc chọn "Gửi ngay sau khi tạo"'
                            : '💡 Thông báo đặt lịch sẽ được hệ thống tự động gửi vào đúng thời gian bạn đã chọn'
                    }
                    className="w-full"
                />
            </div>
        </Dialog>
    );
};

export default CpnCreateEditNotification;
